import { Injectable } from "@angular/core";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { Navigation } from "../model/navigation";
import { SingleChoice } from "../model/singlechoice";
import { Location } from "@angular/common";
@Injectable({
  providedIn: "root",
})
export class NavigationHelper {
  private theTopic: BehaviorSubject<string>;
  private theId: BehaviorSubject<string>;
  private theAction: BehaviorSubject<string>;
  private fragmentTopic: BehaviorSubject<string>;
  private thefragmentId: BehaviorSubject<string>;
  private theFragmentAction: BehaviorSubject<string>;
  private choice: BehaviorSubject<SingleChoice>;
  private ProcesseceUrl: boolean = false;
  private firstTime: boolean = true;
  public history: string[] = [];
  private ignoreNextNavigationTo: string | null = null;
  private lastUrl: string = "";
  private isNavigatingBack = false;

  constructor(public router: Router, private location: Location) {
    this.theTopic = new BehaviorSubject("");
    this.theId = new BehaviorSubject("");
    this.theAction = new BehaviorSubject("");
    this.fragmentTopic = new BehaviorSubject("");
    this.thefragmentId = new BehaviorSubject("");
    this.choice = new BehaviorSubject(SingleChoice.EMPTY);

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.isNavigatingBack) {
          return;
        }

        const url = this.normalizeUrl(event.urlAfterRedirects);
        const lastInHistory = this.normalizeUrl(
          this.history[this.history.length - 1] || ""
        );

        if (
          url !== "" &&
          url !== lastInHistory &&
          url !== this.ignoreNextNavigationTo
        ) {
          this.history.push(url);
          this.lastUrl = url;

          if (this.history.length >= 2) {
            const last = this.history[this.history.length - 1];
            const secondLast = this.history[this.history.length - 2];
            if (this.normalizeUrl(last) === this.normalizeUrl(secondLast)) {
              this.history.pop();
            }
          }
        } else {
        }

        if (url === this.ignoreNextNavigationTo) {
          this.ignoreNextNavigationTo = null;
        }

        this.isNavigatingBack = false;
      }
    });
  }

  public get currentFragmentTopic(): string {
    return this.fragmentTopic.value;
  }

  public get currentTopic(): string {
    return this.theTopic.value;
  }
  public get currentId(): string {
    return this.theId.value;
  }
  public get currentAction(): string {
    return this.theAction.value;
  }

  public get current(): Navigation {
    const nav = new Navigation(
      this.theTopic.value,
      this.theId.value,
      this.theAction.value,
      this.fragmentTopic.value,
      this.thefragmentId.value
    );

    return nav;
  }
  public get freshNavigation() {
    this.selectFromRouter();
    return this.current;
  }

  public get topic(): Observable<string> {
    return this.theTopic;
  }
  public get id(): Observable<string> {
    return this.theId;
  }

  public getLastSuccessfulNavigationSegments(): string[] {
    try {
      const segments =
        this.router.lastSuccessfulNavigation?.finalUrl?.root?.children?.[
          "primary"
        ]?.segments;
      return segments ? segments.map((segment) => segment.path) : [];
    } catch (error) {
      return [];
    }
  }

  public refresh() {
    this.selectFromRouter();
  }

  private selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (this.ProcesseceUrl) {
      return;
    }
    this.ProcesseceUrl = true;

    this.theTopic.next("" + (segments[0] || ""));
    this.theId.next("" + (segments[1] || ""));
    this.theAction.next("" + (segments[2] || ""));
    this.fragmentTopic.next("" + (segments[3] || ""));
    this.thefragmentId.next("" + (segments[4] || ""));

    this.ProcesseceUrl = false;
  }

  public get currentFragmentAction(): Observable<string> {
    return this.theFragmentAction;
  }
  public get fragmentId(): Observable<string> {
    return this.thefragmentId;
  }
  public get fragmentTopics(): Observable<string> {
    return this.fragmentTopic;
  }
  public get singlechoice(): Observable<SingleChoice> {
    return this.choice;
  }
  public get currentChoice(): SingleChoice {
    return this.choice.value;
  }

  public startChoice(choice: SingleChoice) {
    this.choice.next(choice);
  }
  private normalizeUrl(url: string): string {
    return url.split(/[?#]/)[0].replace(/\/+$/, "");
  }
  async back(): Promise<void> {
    if (this.history.length <= 1) {
      this.location.back();
      return;
    }

    const currentUrl = this.history[this.history.length - 1];

    let previousUrl: string | undefined;
    for (let i = this.history.length - 2; i >= 0; i--) {
      if (!this.shouldIgnore(this.history[i])) {
        previousUrl = this.history[i];
        break;
      } else {
      }
    }

    if (!previousUrl) {
      this.location.back();
      return;
    }

    await this.returnTo(previousUrl, () => this.location.back());
  }

  /**
   * Leaves a pick list for the screen it was opened from, as its close button
   * does (#184). Unlike back() it never hands over to the browser history: in
   * the Android app the page is served from http://localhost, which only knows
   * index.html, so a history step that reloads the page asks for a url that is
   * not there, and Cordova then closes the app with an error.
   */
  async closeChoice(): Promise<void> {
    let previousUrl: string | undefined;
    for (let i = this.history.length - 1; i >= 0; i--) {
      if (!this.shouldIgnore(this.history[i])) {
        previousUrl = this.history[i];
        break;
      }
    }

    if (previousUrl) {
      await this.returnTo(previousUrl, () => this.router.navigateByUrl("/tickets"));
    } else {
      // Nothing recorded to go back to: the ticket list is always there.
      await this.router.navigateByUrl("/tickets");
    }
  }

  private async returnTo(previousUrl: string, otherwise: () => void): Promise<void> {
    this.isNavigatingBack = true;
    this.ignoreNextNavigationTo = previousUrl;

    try {
      await this.router.navigateByUrl(previousUrl);

      this.history = this.history.slice(
        0,
        this.history.indexOf(previousUrl) + 1
      );
      this.selectFromRouter();
    } catch (error) {
      otherwise();
    } finally {
      // Done going back, so the next navigation is recorded again. Only the
      // mobile frame used to say so, reading the flag as the navigation ends,
      // before this; on desktop and tablet the history stopped growing after
      // the first back, and the next back went to a page long gone (#179).
      this.isNavigatingBack = false;
    }
  }

  private shouldIgnore(url: string): boolean {
    const rotasIgnoradas = ["choose", "ignoreThisRoute"];
    return rotasIgnoradas.some((r) => url.includes(r));
  }

  public clearNavigationState(): void {
    this.history = [];
    this.theTopic.next("");
    this.theId.next("");
    this.theAction.next("");
    this.fragmentTopic.next("");
    this.thefragmentId.next("");

    this.choice.next(SingleChoice.EMPTY);
    this.ProcesseceUrl = false;
    this.firstTime = true;
  }
  public get navigatingBack(): boolean {
    return this.isNavigatingBack;
  }
  public finishBackNavigation() {
    this.isNavigatingBack = false;
  }
}
